interface AppDownloadSectionProps {
  qrCodeUrl?: string;
}

export default function AppDownloadSection({ qrCodeUrl }: AppDownloadSectionProps) {
  return (
    <section className="py-20 px-6 lg:px-12 bg-charcoal">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* Left: Text */}
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-4 py-1 bg-gold/20 text-gold text-xs tracking-widest uppercase rounded-full mb-6">
              Mobile App
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-wide mb-6 leading-tight">
              Book, Track &amp; Manage<br />
              <span className="text-gold">From Your Phone</span>
            </h2>
            <p className="text-white/60 text-lg font-light leading-relaxed mb-8 max-w-lg">
              Download the IV Hydration Club app to book sessions, track your wellness history, manage memberships, and get exclusive member-only offers — all in one place.
            </p>

            {/* Feature list */}
            <ul className="space-y-3 mb-10">
              {[
                { icon: 'ri-calendar-check-line', text: 'Book & reschedule sessions instantly' },
                { icon: 'ri-history-line', text: 'View your full treatment history' },
                { icon: 'ri-vip-crown-line', text: 'Manage your membership & perks' },
                { icon: 'ri-notification-3-line', text: 'Real-time RN arrival notifications' },
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-gold/15 rounded-full flex-shrink-0">
                    <i className={`${item.icon} text-gold text-sm`}></i>
                  </div>
                  <span className="text-white/70 text-sm font-light">{item.text}</span>
                </li>
              ))}
            </ul>

            {/* Store badges */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <a
                href="#"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-5 py-3 transition-all duration-300 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-apple-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/50 text-[9px] tracking-widest uppercase">Download on the</p>
                  <p className="text-white text-sm font-light tracking-wide">App Store</p>
                </div>
              </a>
              <a
                href="#"
                className="flex items-center gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-5 py-3 transition-all duration-300 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-google-play-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/50 text-[9px] tracking-widest uppercase">Get it on</p>
                  <p className="text-white text-sm font-light tracking-wide">Google Play</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: QR Code */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="bg-white rounded-3xl p-8 flex flex-col items-center gap-4 w-64">
              {/* QR Code placeholder or actual image */}
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Scan to download the IV Hydration Club app"
                  className="w-44 h-44 object-contain"
                />
              ) : (
                <div className="w-44 h-44 bg-[#f5f2ee] rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-[#e8e2da] relative overflow-hidden">
                  {/* QR code pattern simulation */}
                  <div className="absolute inset-3 grid grid-cols-7 gap-0.5 opacity-30">
                    {Array.from({ length: 49 }).map((_, i) => (
                      <div
                        key={i}
                        className={`rounded-sm ${
                          [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,47,48,8,15,22,29,36,10,17,24,31,38].includes(i)
                            ? 'bg-charcoal'
                            : 'bg-transparent'
                        }`}
                      ></div>
                    ))}
                  </div>
                  <div className="relative z-10 flex flex-col items-center gap-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-charcoal rounded-lg">
                      <i className="ri-qr-code-line text-white text-xl"></i>
                    </div>
                    <p className="text-charcoal text-[10px] font-light tracking-wide text-center leading-tight">
                      Add your QR<br />code here
                    </p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-charcoal text-sm font-light tracking-wide mb-1">Scan to Download</p>
                <p className="text-greige text-xs font-light">iOS &amp; Android</p>
              </div>

              {/* Gold accent line */}
              <div className="w-12 h-0.5 bg-gold rounded-full"></div>

              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-gold text-sm"></i>
                </div>
                <p className="text-charcoal text-xs font-light">4.9 · 2,400+ Reviews</p>
              </div>
            </div>

            <p className="text-white/40 text-xs font-light mt-4 text-center max-w-[200px] leading-relaxed">
              Point your camera at the QR code to download instantly
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}
